(function () {
  const DESKTOP_MIN_WIDTH = 769;
  const MOBILE_GAP_FALLBACK = 18;
  const DESKTOP_SCROLL_ALIGN_TOLERANCE = 4;

  function isDesktopDrawer() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  function getDesktopScrollStep(scrollEl) {
    const item = scrollEl.querySelector('.grid-item');
    if (!item) return 0;

    const grid = scrollEl.querySelector('.product-grid');
    const gap = grid ? parseFloat(getComputedStyle(grid).rowGap || getComputedStyle(grid).gap) || 0 : 0;

    return item.offsetHeight + gap;
  }

  function scrollDesktopByStep(scrollEl, direction) {
    const step = getDesktopScrollStep(scrollEl);
    if (!step) return;

    scrollEl.scrollBy({
      top: direction * step,
      behavior: 'smooth',
    });
  }

  function setDesktopNavVisible(navEl, visible) {
    navEl.classList.toggle('is-hidden', !visible);
    navEl.setAttribute('aria-hidden', visible ? 'false' : 'true');

    const btn = navEl.querySelector('button');
    if (btn) btn.disabled = !visible;
  }

  function updateDesktopNav(scrollEl, upNavEl, downNavEl) {
    if (!isDesktopDrawer()) {
      setDesktopNavVisible(upNavEl, false);
      setDesktopNavVisible(downNavEl, false);
      return;
    }

    const canScroll = scrollEl.scrollHeight > scrollEl.clientHeight + 1;

    if (!canScroll) {
      setDesktopNavVisible(upNavEl, false);
      setDesktopNavVisible(downNavEl, false);
      return;
    }

    const atTop = scrollEl.scrollTop <= DESKTOP_SCROLL_ALIGN_TOLERANCE;
    const atBottom =
      scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - DESKTOP_SCROLL_ALIGN_TOLERANCE;

    setDesktopNavVisible(upNavEl, !atTop);
    setDesktopNavVisible(downNavEl, !atBottom);
  }

  function getMobileGap(scrollEl) {
    const grid = scrollEl.querySelector('.product-grid');
    if (!grid) return MOBILE_GAP_FALLBACK;

    const gap = parseFloat(getComputedStyle(grid).columnGap || getComputedStyle(grid).gap);
    return Number.isFinite(gap) ? gap : MOBILE_GAP_FALLBACK;
  }

  function getLastPageScrollLeft(scrollEl) {
    const items = scrollEl.querySelectorAll('.grid-item');
    const count = items.length;
    if (count <= 2) return 0;

    const gap = getMobileGap(scrollEl);
    const itemWidth = (scrollEl.clientWidth - gap) / 2;
    const lastPageFirstIndex = Math.min(Math.floor((count - 1) / 2) * 2, count - 1);

    return lastPageFirstIndex * (itemWidth + gap);
  }

  function setMobileItemWidths(scrollEl) {
    const items = scrollEl.querySelectorAll('.grid-item');
    if (!items.length) return;

    const itemWidth = (scrollEl.clientWidth - getMobileGap(scrollEl)) / 2;

    items.forEach((item) => {
      item.style.flex = `0 0 ${itemWidth}px`;
      item.style.width = `${itemWidth}px`;
      item.style.maxWidth = `${itemWidth}px`;
    });
  }

  // RTL scrollers report scrollLeft as 0 at the start and negative when scrolled.
  function isRtl(scrollEl) {
    return getComputedStyle(scrollEl).direction === 'rtl';
  }

  function getScrollPosition(scrollEl) {
    return Math.abs(scrollEl.scrollLeft);
  }

  function toScrollLeft(scrollEl, position) {
    return isRtl(scrollEl) ? -position : position;
  }

  function getMobilePageCount(scrollEl) {
    return Math.ceil(scrollEl.querySelectorAll('.grid-item').length / 2);
  }

  function getMobilePageScrollLeft(scrollEl, pageIndex) {
    const pageWidth = scrollEl.clientWidth;
    if (!pageWidth || pageIndex <= 0) return 0;

    const pageCount = getMobilePageCount(scrollEl);
    const lastPageIndex = pageCount - 1;

    if (pageIndex >= lastPageIndex) {
      return getLastPageScrollLeft(scrollEl);
    }

    return pageIndex * pageWidth;
  }

  function getMobilePageIndex(scrollEl) {
    const pageWidth = scrollEl.clientWidth;
    if (!pageWidth) return 0;

    const pageCount = getMobilePageCount(scrollEl);
    if (pageCount <= 1) return 0;

    const maxScroll = getLastPageScrollLeft(scrollEl);
    const scrollLeft = getScrollPosition(scrollEl);

    if (scrollLeft >= maxScroll - pageWidth * 0.25) {
      return pageCount - 1;
    }

    if (scrollLeft <= pageWidth * 0.25) {
      return 0;
    }

    return Math.max(0, Math.min(Math.round(scrollLeft / pageWidth), pageCount - 1));
  }

  function updateMobileDots(scrollEl, dotsEl) {
    if (!dotsEl) return;

    const dots = dotsEl.querySelectorAll('[data-cart-recommendations-dot]');
    const activeIndex = getMobilePageIndex(scrollEl);

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  function scrollMobileToPage(scrollEl, pageIndex, behavior) {
    const pageWidth = scrollEl.clientWidth;
    if (!pageWidth) return;

    const pageCount = getMobilePageCount(scrollEl);
    const clampedIndex = Math.max(0, Math.min(pageIndex, pageCount - 1));
    const target = getMobilePageScrollLeft(scrollEl, clampedIndex);

    scrollEl.scrollTo({
      left: toScrollLeft(scrollEl, target),
      behavior: behavior || 'smooth',
    });
  }

  function snapMobileToNearestPage(scrollEl) {
    const pageWidth = scrollEl.clientWidth;
    if (!pageWidth) return;

    const pageIndex = getMobilePageIndex(scrollEl);
    const target = getMobilePageScrollLeft(scrollEl, pageIndex);

    if (Math.abs(getScrollPosition(scrollEl) - target) > 1) {
      scrollEl.scrollTo({
        left: toScrollLeft(scrollEl, target),
        behavior: 'auto',
      });
    }
  }

  function initMobileCarousel(root, scrollEl, dotsEl) {
    const grid = scrollEl.querySelector('.product-grid');

    const refreshMobile = () => {
      if (isDesktopDrawer()) {
        scrollEl.scrollLeft = 0;
        if (grid) {
          grid.querySelectorAll('.grid-item').forEach((item) => {
            item.style.flex = '';
            item.style.width = '';
            item.style.maxWidth = '';
          });
        }
        return;
      }

      setMobileItemWidths(scrollEl);
      updateMobileDots(scrollEl, dotsEl);
    };

    if (dotsEl) {
      dotsEl.querySelectorAll('[data-cart-recommendations-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const pageIndex = Number(dot.dataset.cartRecommendationsDot);
          scrollMobileToPage(scrollEl, pageIndex);
        });
      });
    }

    let snapTimer;
    let isSnapping = false;

    scrollEl.addEventListener(
      'scroll',
      () => {
        if (isDesktopDrawer() || isSnapping) return;

        updateMobileDots(scrollEl, dotsEl);
        window.clearTimeout(snapTimer);
        snapTimer = window.setTimeout(() => {
          isSnapping = true;
          snapMobileToNearestPage(scrollEl);
          window.requestAnimationFrame(() => {
            isSnapping = false;
          });
        }, 80);
      },
      { passive: true }
    );

    // Note: resize + ResizeObserver are registered once in initRecommendations
    // (its `refresh()` delegates here on mobile), so we don't duplicate them.

    refreshMobile();
    return refreshMobile;
  }

  function initRecommendations(root) {
    if (root.dataset.cartRecommendationsInit === 'true') return;

    const scrollEl = root.querySelector('[data-cart-recommendations-scroll]');
    const upNavEl = root.querySelector('[data-cart-recommendations-nav-up]');
    const downNavEl = root.querySelector('[data-cart-recommendations-nav-down]');
    const upBtn = root.querySelector('[data-cart-recommendations-up]');
    const downBtn = root.querySelector('[data-cart-recommendations-down]');
    const dotsEl = root.querySelector('[data-cart-recommendations-dots]');

    if (!scrollEl || !upNavEl || !downNavEl || !upBtn || !downBtn) return;

    root.dataset.cartRecommendationsInit = 'true';

    const refreshMobile = initMobileCarousel(root, scrollEl, dotsEl);
    root._cartRecsRefreshMobile = refreshMobile;

    const refresh = () => {
      if (isDesktopDrawer()) {
        updateDesktopNav(scrollEl, upNavEl, downNavEl);
      } else if (refreshMobile) {
        refreshMobile();
      }
    };

    root._cartRecsRefresh = refresh;

    upBtn.addEventListener('click', () => {
      if (!isDesktopDrawer()) return;
      scrollDesktopByStep(scrollEl, -1);
    });

    downBtn.addEventListener('click', () => {
      if (!isDesktopDrawer()) return;
      scrollDesktopByStep(scrollEl, 1);
    });

    scrollEl.addEventListener(
      'scroll',
      () => {
        if (isDesktopDrawer()) {
          updateDesktopNav(scrollEl, upNavEl, downNavEl);
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', refresh);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        refresh();
      });
      resizeObserver.observe(scrollEl);

      const grid = scrollEl.querySelector('.product-grid');
      if (grid) resizeObserver.observe(grid);
    }

    refresh();
  }

  function initAll() {
    document
      .querySelectorAll('.cart-recommendations[data-location="drawer"]')
      .forEach(initRecommendations);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('cart:updated', () => {
    document
      .querySelectorAll('.cart-recommendations[data-location="drawer"]')
      .forEach((root) => {
        if (root.dataset.cartRecommendationsInit !== 'true') {
          initRecommendations(root);
          return;
        }

        // While the drawer is closed on mobile the scroll container has zero
        // width, so a mobile refresh would compute negative item widths. The
        // drawer-open observer refreshes once it opens.
        if (!isDesktopDrawer() && !document.documentElement.classList.contains('cart-open')) {
          return;
        }

        window.requestAnimationFrame(() => {
          if (typeof root._cartRecsRefresh === 'function') {
            root._cartRecsRefresh();
          }
        });
      });
  });

  const cartOpenObserver = new MutationObserver(() => {
    if (!document.documentElement.classList.contains('cart-open')) return;

    document
      .querySelectorAll('.cart-recommendations[data-location="drawer"]')
      .forEach((root) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (typeof root._cartRecsRefresh === 'function') {
              root._cartRecsRefresh();
            }
          });
        });
      });
  });

  cartOpenObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
})();
